import { useEffect, useId, useRef, useState } from 'react';
import {
  usePwaSettings,
  useResetAppIcons,
  useSavePwaSettings,
  useUploadAppIcons,
} from '@/application/usecases/useAdmin';
import { setAppIconVersion } from '@/shared/branding/appLogo';
import { env } from '@/shared/config/env';
import { isHexColor, makeAppIcons } from '@/shared/image/appIcons';
import { useConfirmAction } from '@/features/shell/useConfirmAction';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import settingsStyles from './SettingsScreen.module.css';
import styles from './AppIconCard.module.css';

/** Where each preview comes from: the uploaded set, or the built-in icons in public/. */
function previews(version: string | null) {
  const base = version === null ? '' : `${env.apiBaseUrl}/pwa/icons/${version}`;
  return {
    icon: `${base}/icon-192.png`,
    maskable: `${base}/icon-maskable-512.png`,
    apple: `${base}/apple-touch-icon.png`,
  };
}

/**
 * The installed app's icon and colours. Request of 5 October 2026.
 *
 * The icons are made here, in the browser, from one logo: four PNGs of the
 * exact sizes the API checks for. The API keeps them under a new version on
 * every upload, so a phone never holds an old picture under a new name, and
 * builds the manifest from what was saved.
 *
 * Choosing a colour or a file changes nothing yet. The four buttons that do
 * are each asked about first: the request of 5 October 2026 (third),
 * section 2.
 */
export function AppIconCard() {
  const settings = usePwaSettings();
  const saveColors = useSavePwaSettings();
  const upload = useUploadAppIcons();
  const reset = useResetAppIcons();
  const ask = useConfirmAction();
  const themeId = useId();
  const backgroundId = useId();
  const logoInput = useRef<HTMLInputElement>(null);
  const maskableInput = useRef<HTMLInputElement>(null);

  const [theme, setTheme] = useState('');
  const [background, setBackground] = useState('');
  const [logo, setLogo] = useState<File | null>(null);
  const [maskable, setMaskable] = useState<File | null>(null);
  const [making, setMaking] = useState(false);

  const data = settings.data;
  useEffect(() => {
    if (data === undefined) return;
    setTheme(data.themeColor);
    setBackground(data.backgroundColor);
  }, [data]);

  // The menu, the sign-in card and the browser tab follow an upload at once.
  // Request of 6 October 2026.
  const iconVersion = data?.iconVersion;
  useEffect(() => {
    if (iconVersion !== undefined) setAppIconVersion(iconVersion);
  }, [iconVersion]);

  if (data === undefined) return null;

  const colorsValid = isHexColor(theme) && isHexColor(background);
  const colorsChanged =
    theme.toLowerCase() !== data.themeColor || background.toLowerCase() !== data.backgroundColor;
  const busy = saveColors.isPending || upload.isPending || reset.isPending || making;
  const shown = previews(data.iconVersion);

  function submitColors(
    themeColor: string,
    backgroundColor: string,
    statement: string,
    verb: string,
  ) {
    ask(
      statement,
      () =>
        saveColors
          .mutateAsync({ themeColor, backgroundColor })
          .then(() => toast(strings.appIcon.colorsSaved)),
      { title: strings.appIcon.title, confirmLabel: verb },
    );
  }

  /** Makes the four pictures from the logo and sends them. A refusal by the server is thrown. */
  async function sendIcons(source: File) {
    setMaking(true);
    let icons: Awaited<ReturnType<typeof makeAppIcons>>;
    try {
      icons = await makeAppIcons(source, data?.backgroundColor ?? background, maskable ?? undefined);
    } catch {
      toast(strings.appIcon.unreadable);
      return;
    } finally {
      setMaking(false);
    }
    await upload.mutateAsync(icons);
    toast(strings.appIcon.uploaded);
    setLogo(null);
    setMaskable(null);
  }

  function uploadIcons() {
    if (logo === null) return;
    ask(strings.confirm.appIcons(logo.name), () => sendIcons(logo), {
      title: strings.appIcon.title,
      confirmLabel: strings.appIcon.upload,
    });
  }

  return (
    <section className={settingsStyles.card} aria-labelledby={`${themeId}-title`}>
      <h2 id={`${themeId}-title`} className={settingsStyles.legend}>
        {strings.appIcon.title}
      </h2>
      <p className={settingsStyles.hint}>{strings.appIcon.hint}</p>

      {!window.isSecureContext && (
        <p className={styles.warning} role="note">
          {strings.appIcon.insecure}
        </p>
      )}

      <div className={styles.previews} aria-label={strings.appIcon.current}>
        <figure>
          <img src={shown.icon} alt="" width={64} height={64} />
          <figcaption>{strings.appIcon.preview192}</figcaption>
        </figure>
        <figure>
          <img className={styles.masked} src={shown.maskable} alt="" width={64} height={64} />
          <figcaption>{strings.appIcon.previewMaskable}</figcaption>
        </figure>
        <figure>
          <img className={styles.rounded} src={shown.apple} alt="" width={64} height={64} />
          <figcaption>{strings.appIcon.previewApple}</figcaption>
        </figure>
      </div>
      {data.iconVersion === null && (
        <p className={settingsStyles.hint}>{strings.appIcon.builtIn}</p>
      )}

      <form
        className={styles.colors}
        onSubmit={(event) => {
          event.preventDefault();
          if (colorsValid) {
            submitColors(
              theme.toLowerCase(),
              background.toLowerCase(),
              strings.confirm.appColors(theme.toLowerCase(), background.toLowerCase()),
              strings.appIcon.saveColors,
            );
          }
        }}
      >
        <ColorField
          id={themeId}
          label={strings.appIcon.themeColor}
          hint={strings.appIcon.themeColorHint}
          value={theme}
          disabled={busy}
          onChange={setTheme}
        />
        <ColorField
          id={backgroundId}
          label={strings.appIcon.backgroundColor}
          hint={strings.appIcon.backgroundColorHint}
          value={background}
          disabled={busy}
          onChange={setBackground}
        />
        {!colorsValid && (
          <p className={settingsStyles.dayEndError} role="alert">
            {strings.appIcon.badColor}
          </p>
        )}
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={busy || !colorsValid || !colorsChanged}>
            {strings.appIcon.saveColors}
          </Button>
          <Button
            variant="text"
            disabled={
              busy ||
              (data.themeColor === data.defaultThemeColor &&
                data.backgroundColor === data.defaultBackgroundColor)
            }
            onClick={() =>
              submitColors(
                data.defaultThemeColor,
                data.defaultBackgroundColor,
                strings.confirm.resetAppColors,
                strings.appIcon.resetColors,
              )
            }
          >
            {strings.appIcon.resetColors}
          </Button>
        </div>
      </form>

      <div className={styles.uploads}>
        <div>
          <span className={styles.label}>{strings.appIcon.logo}</span>
          <p className={settingsStyles.hint}>{strings.appIcon.logoHint}</p>
          <input
            ref={logoInput}
            className={styles.file}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            aria-label={strings.appIcon.chooseLogo}
            onChange={(event) => setLogo(event.target.files?.[0] ?? null)}
          />
          <Button disabled={busy} onClick={() => logoInput.current?.click()}>
            {strings.appIcon.chooseLogo}
          </Button>
          {logo !== null && <span className={styles.fileName}>{logo.name}</span>}
        </div>
        <div>
          <span className={styles.label}>{strings.appIcon.maskable}</span>
          <p className={settingsStyles.hint}>{strings.appIcon.maskableHint}</p>
          <input
            ref={maskableInput}
            className={styles.file}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            aria-label={strings.appIcon.chooseMaskable}
            onChange={(event) => setMaskable(event.target.files?.[0] ?? null)}
          />
          <Button disabled={busy} onClick={() => maskableInput.current?.click()}>
            {strings.appIcon.chooseMaskable}
          </Button>
          {maskable !== null && (
            <>
              <span className={styles.fileName}>{maskable.name}</span>
              <Button
                variant="text"
                disabled={busy}
                onClick={() => {
                  setMaskable(null);
                  if (maskableInput.current !== null) maskableInput.current.value = '';
                }}
              >
                {strings.appIcon.clearMaskable}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className={styles.actions}>
        <Button
          variant="primary"
          disabled={busy || logo === null}
          onClick={uploadIcons}
        >
          {making || upload.isPending ? strings.appIcon.uploading : strings.appIcon.upload}
        </Button>
        <Button
          variant="text"
          disabled={busy || data.iconVersion === null}
          onClick={() =>
            ask(
              strings.confirm.resetAppIcons,
              () => reset.mutateAsync().then(() => toast(strings.appIcon.resetDone)),
              { title: strings.appIcon.title, confirmLabel: strings.appIcon.reset },
            )
          }
        >
          {strings.appIcon.reset}
        </Button>
      </div>

      <p className={styles.note}>{strings.appIcon.updates}</p>
    </section>
  );
}

function ColorField({
  id,
  label,
  hint,
  value,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className={styles.color}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      <div className={styles.colorRow}>
        <input
          className={styles.swatch}
          type="color"
          aria-label={`${label}, picker`}
          value={isHexColor(value) ? value.toLowerCase() : '#000000'}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
        <input
          id={id}
          className={styles.hex}
          type="text"
          inputMode="text"
          spellCheck={false}
          maxLength={7}
          aria-describedby={`${id}-hint`}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value.trim())}
        />
      </div>
      <p id={`${id}-hint`} className={styles.small}>
        {hint}
      </p>
    </div>
  );
}
